import Column from "@/Components/Grid/Column";
import Container from "@/Components/Grid/Container";
import Row from "@/Components/Grid/Row";
import useIsServer from "@/Hooks/useIsServer";
import useMediaQuery from "@/Hooks/useMediaQuery";
import PatientNavigationMenu from "@/Partials/Patient/PatientNavigationMenu";
import PatientSearchMenu from "@/Partials/Patient/PatientSearchMenu";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, router } from "@inertiajs/react";
import { PropsWithChildren, useEffect, useState } from "react";

const PatientLayout = ({ children, withMonogram = false, category = '' }: PropsWithChildren & {
    withMonogram?: boolean
    category?: string
}) => {
    useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            return;
        }

        return router.on('navigate', (event) => {
            gtag('event', 'page_view', {
                page_location: event.detail.page.url,
                page_title: event.detail.page.component,
            });
        });
    }, []);

    const pageGradient = {
        'onkologia': 'bg-pageOncology',
        'hematologia': 'bg-pageHematology',
        'reumatologia': 'bg-pageHematology',
        'neurologia': 'bg-pageNeurology',
        'psychiatria': 'bg-pagePsychology',
        'urologia': 'bg-pageUrology',
        'osteoporoza': 'bg-pageOsteoporosis',
        '': 'bg-gradientMain',
    }[category];

    const [isShownNavigationMenu, setIsShownNavigationMenu] = useState<boolean>(false);
    const [isShownSearchMenu, setIsShownSearchMenu] = useState<boolean>(false);
    const [scrolled, setScrolled] = useState<boolean>(false);
    const isServer = useIsServer();
    const isMobile = useMediaQuery();

    useEffect(() => {
        const throttle = <T extends (...args: any[]) => void>(func: T, limit: number) => {
            let inThrottle: boolean;
            return function (this: any, ...args: Parameters<T>) {
                const context = this;
                if (!inThrottle) {
                    func.apply(context, args);
                    inThrottle = true;
                    setTimeout(() => (inThrottle = false), limit);
                }
            };
        };

        const handleScrollThrottled = throttle(() => {
            const scrollY = window.scrollY;
            const threshold = !isMobile ? 250 : 10;

            if (scrollY > threshold) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        }, 30);

        if (!isServer) {
            window.addEventListener('scroll', handleScrollThrottled);
        }

        return () => {
            if (!isServer) {
                window.removeEventListener('scroll', handleScrollThrottled);
            }
        };
    }, [isServer]);

    return (
        <div className={`min-h-screen ${pageGradient} relative flex flex-col justify-center`}>
            {withMonogram &&
                <div className='absolute top-0 w-full z-10 h-[52.3%] max-h-[630px] object-cover object-center bg-[url(@static/accord-symbol-pattern.svg)]'></div>
            }
            <header className={`transition-theme ${!scrolled ? 'md:py-6 pt-6 pb-4' : 'py-4 bg-deepIndigo/70'} sticky top-0 z-50`}>
                <Container className="relative z-20">
                    <Row className='justify-between'>
                        <Column xl={2} lg={2} md={2} sm={4} xs={5}>
                            <Link href='/dla-pacjenta' title='Strona główna'>
                                <img src='/storage/static/accord-logo-full.svg' className='maxMd:max-w-[105px] max-w-40 w-full h-auto' />
                            </Link>
                        </Column>
                        <Column xl={8} lg={8} md={8} sm={8} xs={7} className='flex justify-end'>
                            <div className="flex flex-wrap gap-x-6 gap-y-4 justify-end">
                                <button onClick={() => setIsShownSearchMenu((prev) => !prev)} type='button' className='w-10 h-10 flex items-center justify-center p-2 bg-accordorange rounded-full transition-theme hover:opacity-75'>
                                    {isShownSearchMenu ?
                                        <Icon icon={`ic:round-close`} width={24} className='text-arcticMist' />
                                        :
                                        <Icon icon={`ph:magnifying-glass-bold`} width={24} className='text-arcticMist' />
                                    }
                                </button>
                                <button type='button' onClick={() => setIsShownNavigationMenu((prev) => !prev)} className='w-10 h-10 flex items-center justify-center p-2 bg-royalPeriwinkle rounded-full transition-theme hover:opacity-75'>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <rect x="6" y="17" width="16" height="2" rx="1" fill="#E8EEF2" />
                                        <rect x="4" y="11" width="18" height="2" rx="1" fill="#E8EEF2" />
                                        <rect x="2" y="5" width="20" height="2" rx="1" fill="#E8EEF2" />
                                    </svg>
                                </button>
                            </div>
                        </Column>
                    </Row>
                </Container>
                <PatientNavigationMenu
                    state={isShownNavigationMenu}
                    setState={setIsShownNavigationMenu}
                />
                <PatientSearchMenu
                    state={isShownSearchMenu}
                    setState={setIsShownSearchMenu}
                />
            </header>



            <main className='relative z-20'>
                {children}
            </main>

            <footer className='px-14 maxMd:py-4 flex flex-wrap maxMd:gap-y-4 justify-center z-20 relative py-5 items-center gap-x-10 mt-auto'>
                <Link href={route('front.privacyPolicy')} target='_blank' title='Polityka prywatności' className='text-royalPeriwinkle text-16 leading-6 tracking-[0.24px] maxMd:w-full maxMd:text-center'>
                    Polityka prywatności
                </Link>
                <Link href={route('front.terms')} target='_blank' title='Regulamin serwisu' className='text-royalPeriwinkle text-16 leading-6 tracking-[0.24px] maxMd:w-full maxMd:text-center'>
                    Regulamin serwisu
                </Link>
                <Link href={route('front.help')} title='Pomoc' className='text-royalPeriwinkle text-16 leading-6 tracking-[0.24px] maxMd:w-full maxMd:text-center'>
                    Pomoc
                </Link>
                <a target="_blank" href={`https://www.accord-healthcare.com/pl/`} title='Pomoc' className='md:w-auto w-full inline-block md:text-left text-center md:mt-0 mt-4 text-royalPeriwinkle text-16 tracking-[0.24px] transition-theme hover:text-accordorange'>
                    Mój Accord
                </a>
            </footer>
        </div>
    )
}

export default PatientLayout;